import React, { useState } from 'react';
import { Phone, Mail, Globe, MapPin, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BANNER_IMG = "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1400&q=85";

const serviceOptions = [
  "Custom Home Build", "Commercial Finishing", "Condominium Finishing",
  "Plaster Moulding & Fireplace Mantle", "Residential Renovation", "Home Additions",
  "Kitchen Cabinets & Refinishing", "Painting & Decoration",
  "Flooring & Tiling", "Restoration", "Millwork",
  "Other / Not Listed",
];

const initialForm = { first_name: '', last_name: '', email: '', phone: '', service: '', message: '' };

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState(null); // null | 'loading' | 'success' | 'error'

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.service) {
      setStatus('error');
      return;
    }
    setStatus('loading');
    try {
      await axios.post(`${API}/contact`, form);
      setStatus('success');
      setForm(initialForm);
    } catch {
      setStatus('error');
    }
  };

  const inputClass = "w-full bg-slate-50 border border-slate-200 px-4 py-3 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-[#00A2E8] focus:ring-1 focus:ring-[#00A2E8] transition-colors duration-200";

  return (
    <main data-testid="contact-page">

      {/* BANNER */}
      <section
        className="relative h-72 md:h-96 pt-20 flex items-end"
        style={{ backgroundImage: `url(${BANNER_IMG})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
      >
        <div className="absolute inset-0 bg-slate-950/70" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-12 w-full">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#00A2E8] mb-2">REACH OUT</p>
          <h1 className="text-4xl md:text-5xl font-black uppercase tracking-tighter text-white leading-none">
            Get In Touch
          </h1>
        </div>
      </section>

      {/* CONTACT BODY */}
      <section className="py-20 md:py-32 bg-white" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">

            {/* LEFT: Contact Info */}
            <div className="lg:col-span-2" data-testid="contact-info">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#00A2E8] mb-3">CONTACT INFORMATION</p>
              <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900 mb-6">
                Let's Discuss Your Project
              </h2>
              <p className="text-sm text-slate-600 leading-relaxed mb-8">
              Ready to start your project? Contact us today for a free, no-obligation consultation. We serve all of Ontario and are committed to delivering quality results on time and within budget.
              </p>

              <div className="flex flex-col gap-5">
                <div className="flex items-start gap-4 p-4 border border-slate-200 hover:border-[#00A2E8] transition-colors group">
                  <div className="w-10 h-10 bg-[#00A2E8]/10 flex items-center justify-center shrink-0 group-hover:bg-[#00A2E8] transition-colors">
                    <Phone className="w-5 h-5 text-[#00A2E8] group-hover:text-white transition-colors" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Phone</p>
                    <a href="tel:+14165703834" data-testid="contact-phone-link" className="text-sm font-bold text-slate-900 hover:text-[#00A2E8] transition-colors">
                      +1 416 570 3834
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 border border-slate-200 hover:border-[#00A2E8] transition-colors group">
                  <div className="w-10 h-10 bg-[#00A2E8]/10 flex items-center justify-center shrink-0 group-hover:bg-[#00A2E8] transition-colors">
                    <Mail className="w-5 h-5 text-[#00A2E8] group-hover:text-white transition-colors" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Email</p>
                    <a href="mailto:Jay@ArianaBuild.ca" data-testid="contact-email-link" className="text-sm font-bold text-slate-900 hover:text-[#00A2E8] transition-colors">
                      Jay@ArianaBuild.ca
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 border border-slate-200 hover:border-[#00A2E8] transition-colors group">
                  <div className="w-10 h-10 bg-[#00A2E8]/10 flex items-center justify-center shrink-0 group-hover:bg-[#00A2E8] transition-colors">
                    <Globe className="w-5 h-5 text-[#00A2E8] group-hover:text-white transition-colors" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Website</p>
                    <a href="https://www.ArianaBuild.ca" target="_blank" rel="noopener noreferrer" className="text-sm font-bold text-slate-900 hover:text-[#00A2E8] transition-colors">
                      www.ArianaBuild.ca
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 border border-slate-200 hover:border-[#00A2E8] transition-colors group">
                  <div className="w-10 h-10 bg-[#00A2E8]/10 flex items-center justify-center shrink-0 group-hover:bg-[#00A2E8] transition-colors">
                    <MapPin className="w-5 h-5 text-[#00A2E8] group-hover:text-white transition-colors" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Service Area</p>
                    <p className="text-sm font-bold text-slate-900">Ontario</p>
                    <p className="text-xs text-slate-500 mt-0.5">Toronto · Hamilton · Barrie · Kitchener & more</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 p-5 bg-slate-50 border-l-4 border-[#00A2E8]">
                <p className="text-xs font-bold uppercase tracking-wider text-[#00A2E8] mb-1">Jay Wahid</p>
                <p className="text-sm text-slate-700 font-semibold">Founder & Lead Contractor</p>
                <p className="text-xs text-slate-500 mt-1">Available Monday – Saturday, 8am – 7pm</p>
              </div>
            </div>

            {/* RIGHT: Form */}
            <div className="lg:col-span-3" data-testid="contact-form-section">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#00A2E8] mb-3">REQUEST A QUOTE</p>
              <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900 mb-6">
                Tell Us About Your Project
              </h2>

              {status === 'success' && (
                <div data-testid="form-success" className="flex items-start gap-3 p-5 bg-green-50 border border-green-200 mb-6">
                  <CheckCircle className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-bold text-green-800">Message received!</p>
                    <p className="text-xs text-green-700 mt-1">Thank you for contacting Ariana General Contracting. We'll be in touch within 1 business day.</p>
                  </div>
                </div>
              )}

              {status === 'error' && (
                <div data-testid="form-error" className="flex items-start gap-3 p-5 bg-red-50 border border-red-200 mb-6">
                  <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                  <p className="text-sm text-red-800">Please fill in all required fields and try again.</p>
                </div>
              )}

              <form onSubmit={handleSubmit} data-testid="contact-form" className="flex flex-col gap-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">First Name *</label>
                    <input
                      type="text"
                      name="first_name"
                      value={form.first_name}
                      onChange={handleChange}
                      placeholder="John"
                      required
                      data-testid="input-first-name"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">Last Name *</label>
                    <input
                      type="text"
                      name="last_name"
                      value={form.last_name}
                      onChange={handleChange}
                      placeholder="Smith"
                      required
                      data-testid="input-last-name"
                      className={inputClass}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="john@example.com"
                      required
                      data-testid="input-email"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="+1 (416) 000-0000"
                      data-testid="input-phone"
                      className={inputClass}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">Service Required *</label>
                  <select
                    name="service"
                    value={form.service}
                    onChange={handleChange}
                    required
                    data-testid="input-service"
                    className={`${inputClass} cursor-pointer`}
                  >
                    <option value="">Select a service...</option>
                    {serviceOptions.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">Project Details</label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    placeholder="Tell us about your project — location, scope, timeline, or any questions..."
                    rows={5}
                    data-testid="input-message"
                    className={`${inputClass} resize-none`}
                  />
                </div>

                <button
                  type="submit"
                  disabled={status === 'loading'}
                  data-testid="contact-submit-btn"
                  className="w-full bg-[#00A2E8] hover:bg-[#0088C4] text-white py-4 font-black text-sm tracking-widest transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 h-14 mt-2"
                >
                  {status === 'loading' ? (
                    <><Loader2 className="w-5 h-5 animate-spin" /> SENDING...</>
                  ) : (
                    'SEND MESSAGE'
                  )}
                </button>

                <p className="text-xs text-slate-400 text-center">
                  We'll respond within 1 business day. Your information is kept private.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* BOTTOM STRIP */}
      <section className="bg-[#00A2E8] py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-white font-bold text-sm">Free Consultations — No Obligation</p>
          <a href="tel:+14165703834" data-testid="contact-bottom-phone" className="flex items-center gap-2 text-white font-black text-lg hover:underline">
            <Phone className="w-5 h-5" /> +1 416 570 3834
          </a>
        </div>
      </section>

    </main>
  );
}
